/**
 * Sidebar contextual del módulo Automatización (agentes IA) — patrón Chatwoot/Linear.
 *
 * En cualquier ruta `/agents/*` el Sidebar global se reemplaza por este, igual que Chats
 * (`WaInboxNavigation`), Llamadas (`CallCenterNavigation`) y Ads (`AdsNavigation`): módulo-as-app
 * con su propio mundo y un «Volver» para salir. Lo decide `AppLayout`. Polaris `<Navigation>` — NO
 * un sidebar custom.
 *
 * Pedido por el founder el 28-ago: «creale un sidebar propio al módulo de agentes así como lo
 * tiene el módulo de chats».
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🚨 24 ENTRADAS → 14 (veredicto del módulo Automatización, 12-sep-2026, §3)
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * Se midió cada entrada en producción y se sacó lo que no tenía uso o duplicaba una puerta:
 *
 *   · Chatbots clásicos — 0 corridas en WhatsApp en toda la historia; motor viejo del widget web.
 *     Sigue en Apps, no acá: dos constructores de «flujos» en el mismo menú era la confusión.
 *   · Cómo rinde el embudo — era EL MISMO componente que abre Flujos, con otro nombre.
 *   · Resultados — la misma tabla que El agente › Rendimiento.
 *   · Pulgares abajo — 0 votos en la historia; el juez automático ya produce la cola de Calidad.
 *   · Atributos del cliente — 0 filas y ningún worker las lee; son «Campos del cliente» sin motor.
 *   · Respuestas rápidas — son del asesor humano, no del agente ni de los flujos. Sólo en Chats.
 *   · Cerebro → se funde en Base de conocimiento (por aprobar) y en El agente › Rendimiento.
 *   · Crear con IA y Catálogo → son los dos caminos del botón «Nuevo agente» en El agente. El
 *     responder corre UN agente activo por canal: crear más no cambia nada.
 *   · Clientes del agente → es depuración («la primera pregunta cuando algo sale raro»): va con
 *     Probar y medir hasta fundirse en la ficha del cliente.
 *
 * Lo que las grandes hacen igual (§8): Salesforce no pasa de 10 subagentes «para no confundir al
 * router»; Nielsen #8: cada unidad de información extra compite con las relevantes.
 *
 * 🪤 LAS RUTAS SE DERIVAN DE `ROUTES`, NO SE LISTAN A MANO. Acá había un array escrito a mano y se
 * quedó atrás: le faltaban las secciones nuevas, así que al abrir una no se iluminaba nada. Toda
 * ruta `/agents/...` entra sola.
 */

import { useLocation } from 'react-router-dom';
import { Navigation } from '@/lib/polaris';
import {
  ArrowLeftIcon,
  AppsIcon,
  AutomationIcon,
  ChatIcon,
  LinkIcon,
  DatabaseIcon,
  HashtagIcon,
  MagicIcon,
  NoteIcon,
  PersonIcon,
  PlayIcon,
  SendIcon,
  TargetIcon,
} from '@/lib/polaris-icons';
import { ROUTES, storePath } from '@/config/routes.config';
import { useAuth } from '@smarty/shared';

export function AgentsNavigation() {
  const location = useLocation();
  const { storeSlug } = useAuth();

  const toUrl = (route: string) =>
    storeSlug ? storePath(storeSlug, route) : route;

  const path = location.pathname || '';

  // La entrada activa es el prefijo MÁS LARGO que matchea: así el detalle de un agente
  // (/agents/studio/:id) ilumina «El agente» y no cualquier otra cosa.
  const ALL = (Object.values(ROUTES) as string[]).filter((r) => r.startsWith('/agents'));
  let activeUrl = '';
  for (const r of ALL) {
    const full = toUrl(r);
    if (
      (path === full || path.startsWith(full + '/')) &&
      full.length > activeUrl.length
    ) {
      activeUrl = full;
    }
  }
  const mk = (route: string, label: string, icon: typeof MagicIcon, badge?: string) => {
    const url = toUrl(route);
    return { url, label, icon, matches: url === activeUrl, ...(badge ? { badge } : {}) };
  };

  /*
   * Flujos primero y solo, como uChat: es a donde se entra a trabajar.
   */
  const flujosItems = [
    mk(ROUTES.AGENTS_FLUJOS, 'Flujos', AppsIcon),
  ];

  /*
   * Lo que arranca un flujo sin que nadie lo toque: el pedido, el anuncio, el link, la palabra, la
   * intención — y la difusión que sale del negocio. Disparadores y Palabras clave comparten el motor
   * (`flow_triggers`) pero son páginas distintas por decisión del founder («son diferentes cosas»).
   * Intenciones va acá y no en el Centro de IA: es la entrada hermana de la palabra clave, por
   * significado en vez de por palabras. Campañas entra acá desde el 13-sep: una difusión es una
   * salida del negocio hacia el cliente, y con una sola entrada no merecía grupo propio.
   */
  const automatizacionItems = [
    mk(ROUTES.AGENTS_DISPARADORES, 'Disparadores', AutomationIcon),
    mk(ROUTES.AGENTS_PALABRAS_CLAVE, 'Palabras clave', ChatIcon),
    mk(ROUTES.AGENTS_INTENCIONES, 'Intenciones', TargetIcon),
    mk(ROUTES.AGENTS_LINK_QR, 'Link y QR', LinkIcon),
    mk(ROUTES.AGENTS_DIFUSIONES, 'Campañas', SendIcon),
  ];

  /*
   * Quién es el agente, qué sabe, y cómo vende cada producto. Una puerta por concepto: «qué sabe»
   * es Base de conocimiento (artículos + lectura automática + lo que aprendió y espera aprobación);
   * los interruptores de herramientas viven en El agente › Conocimiento porque son del agente.
   * «Nuevo agente» (plantilla o describirlo con IA) es un botón de El agente, no un renglón acá.
   */
  const centroDeIaItems = [
    mk(ROUTES.AGENTS_STUDIO, 'El agente', PersonIcon),
    mk(ROUTES.AGENTS_ESTRATEGIA, 'Estrategia comercial', TargetIcon),
    mk(ROUTES.AGENTS_KNOWLEDGE_BASE, 'Base de conocimiento', DatabaseIcon),
  ];

  /*
   * Lo que los flujos y el agente USAN sin ser un flujo: los campos que el agente guarda, las
   * etiquetas que los flujos ponen, las plantillas aprobadas por Meta y los activos (audios, fotos)
   * que el agente manda. Etiquetas y Plantillas nacieron en Chats y siguen entrando por
   * `/agents/contenido/...` para que el sidebar no cambie de mundo (`useVolverDelContenido`).
   */
  const contenidoItems = [
    mk(ROUTES.AGENTS_CAMPOS, 'Campos', HashtagIcon),
    mk(ROUTES.AGENTS_CONTENIDO_ETIQUETAS, 'Etiquetas', HashtagIcon),
    mk(ROUTES.AGENTS_CONTENIDO_PLANTILLAS, 'Plantillas de WhatsApp', NoteIcon),
    mk(ROUTES.AGENTS_ACTIVOS, 'Activos de venta', MagicIcon),
  ];

  // Probar, medir, y ver qué pasó. Es lo que se hace DESPUÉS de armar, y por eso va aparte.
  const pruebaItems = [
    mk(ROUTES.AGENTS_PRUEBAS, 'Conversaciones de prueba', PlayIcon),
    mk(ROUTES.AGENTS_CLIENTES, 'Clientes del agente', PersonIcon),
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Navigation location={location.pathname}>
          <Navigation.Section
            items={[
              {
                label: 'Volver',
                icon: ArrowLeftIcon,
                url: toUrl(ROUTES.HOME),
                matches: false,
              },
            ]}
          />
          <Navigation.Section items={flujosItems} separator />
          <Navigation.Section title="Automatización" items={automatizacionItems} separator />
          <Navigation.Section title="Centro de IA" items={centroDeIaItems} separator />
          <Navigation.Section title="Contenido" items={contenidoItems} separator />
          <Navigation.Section title="Probar y medir" items={pruebaItems} separator />
        </Navigation>
      </div>
    </div>
  );
}
